import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import "./LiveGame.css";
import "./variables.css";
import API_URL from "./config";

/**
 * LiveGame — Quarter-by-quarter game simulation with Moments decisions.
 *
 * Flow:
 *   1. Receives game setup from PlayGame via location.state
 *   2. Calls /simulate-live/start → gets Q1 result + optional moment
 *   3. If moment: show decision card, user picks an option
 *   4. Layer 1: Show narrative confirmation ("You signal for time...")
 *   5. Calls /simulate-live/continue with decision → gets next quarter + feedback
 *   6. Layer 2: Show outcome reveal (positive/negative effects from the quarter)
 *   7. Layer 3: Show callback banners when delayed effects activate
 *   8. Repeats until isComplete === true
 *   9. Navigates to /results with full game data
 */

const CATEGORY_LABELS = {
  momentum_shift: "Momentum Shift",
  player_psychology: "Player Psychology",
  coaching: "Coaching Decision",
  external: "External Factor",
  risk_reward: "Risk vs Reward",
  injury_fatigue: "Injury & Fatigue",
};

const CATEGORY_ICONS = {
  momentum_shift: "\u26A1",
  player_psychology: "\uD83E\uDDE0",
  coaching: "\uD83D\uDCCB",
  external: "\uD83C\uDFDF\uFE0F",
  risk_reward: "\uD83C\uDFB2",
  injury_fatigue: "\uD83E\uDE79",
};

const RiskMeter = ({ risk }) => {
  const levels = risk === "high" ? 3 : risk === "medium" ? 2 : 1;
  return (
    <div className="risk-meter">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className={`risk-meter-bar ${i <= levels ? `filled ${risk}` : ""}`}
          style={{ height: 6 + i * 4 }}
        />
      ))}
    </div>
  );
};

const LiveGame = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const setup = location.state || {};

  // Core state
  const [gameState, setGameState] = useState(null);
  const [phase, setPhase] = useState("loading");
  // Phases: loading, simulating, quarter_result, moment, narrative, outcome, callback, complete
  const [currentMoment, setCurrentMoment] = useState(null);
  const [narrativeText, setNarrativeText] = useState("");
  const [outcomeEffects, setOutcomeEffects] = useState([]);
  const [callbackTexts, setCallbackTexts] = useState([]);
  const [scoreImpact, setScoreImpact] = useState(null);
  const [shaking, setShaking] = useState(false);
  const [error, setError] = useState("");
  const [quarterResults, setQuarterResults] = useState([]);
  const hasStarted = useRef(false);

  // Ref that stays in sync with gameState so setTimeout callbacks
  // always read the latest value instead of a stale closure copy.
  const gameStateRef = useRef(null);

  // Pending decision to send after narrative phase
  const pendingDecision = useRef(null);

  // Start (or resume) the game on mount
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const bootstrap = async () => {
      const saveId = setup.saveId || localStorage.getItem("activeSaveId");
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user._id;

      // Check for an in-progress game for this save.
      if (saveId && userId) {
        try {
          const res = await fetch(`${API_URL}/api/saves/${saveId}/active-live-game`, {
            headers: { "X-User-ID": userId },
          });
          if (res.ok) {
            const data = await res.json();
            if (data.active) {
              resumeGame(data);
              return;
            }
          }
        } catch (err) {
          // Fall through to normal start
        }
      }

      if (!setup.homeTeamId || !setup.awayTeamId) {
        setError("Missing game setup data. Please go back and try again.");
        return;
      }

      startGame();
    };

    bootstrap();
  }, []);

  const resumeGame = (data) => {
    const gs = data.gameState;
    gameStateRef.current = gs;
    setGameState(gs);

    const currentQ = gs?.currentQuarter || 0;

    // If the game was already fully simulated (finalize happened but save failed),
    // re-run finalize via continueGame so the client can navigate to /results.
    if (currentQ >= 4) {
      continueGame();
      return;
    }

    if (data.pendingMoment) {
      setCurrentMoment(data.pendingMoment);
      setPhase("moment");
    } else {
      // Mid-quarter (or between quarters with no pending decision) — just continue.
      continueGame();
    }
  };

  const startGame = async () => {
    try {
      setPhase("simulating");

      const user = JSON.parse(localStorage.getItem("user") || "{}");

      const payload = {
        homeTeamId: setup.homeTeamId,
        awayTeamId: setup.awayTeamId,
        season: setup.season,
        saveId: setup.saveId,
        userId: user._id,
        gameId: setup.gameId,
        homeLineup: setup.homeLineup || null,
        awayLineup: setup.awayLineup || null,
        gameType: setup.gameType || "regular",
      };

      const res = await fetch(`${API_URL}/api/games/simulate-live/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        const detail = data.detail;
        const msg = typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((e) => e.msg || JSON.stringify(e)).join("; ")
            : "Failed to start live game";
        throw new Error(msg);
      }

      const data = await res.json();
      handleServerResponse(data);
    } catch (err) {
      setError(err.message || "Failed to start game");
      setPhase("loading");
    }
  };

  const continueGame = async (momentId = null, choiceId = null) => {
    try {
      if (!gameStateRef.current) {
        // Game state not initialized — retrying start
        await startGame();
        return;
      }

      setPhase("simulating");

      const payload = {
        gameState: gameStateRef.current,
        momentId: momentId,
        choiceId: choiceId,
      };

      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await fetch(`${API_URL}/api/games/simulate-live/continue`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User-ID": user._id || "",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        const detail = data.detail;
        const msg = typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((e) => e.msg || JSON.stringify(e)).join("; ")
            : "Failed to continue game";
        throw new Error(msg);
      }

      const data = await res.json();
      handleServerResponse(data);
    } catch (err) {
      setError(err.message || "Failed to continue game");
    }
  };

  const triggerShake = () => {
    setShaking(true);
    setTimeout(() => setShaking(false), 500);
  };

  const handleServerResponse = (data) => {
    gameStateRef.current = data.gameState;
    setGameState(data.gameState);

    if (data.quarterResult) {
      setQuarterResults((prev) => [...prev, data.quarterResult]);
    }

    // Calculate score impact when we have feedback with effects
    if (data.feedback && data.quarterResult) {
      const gs = data.gameState;
      const userSide = gs?.userSide || "home";
      const qr = data.quarterResult;
      const userQScore = userSide === "home" ? qr.homeScore : qr.awayScore;
      const oppQScore = userSide === "home" ? qr.awayScore : qr.homeScore;
      const delta = userQScore - oppQScore;
      setScoreImpact({
        userScore: userQScore,
        oppScore: oppQScore,
        delta,
        quarter: qr.quarter,
      });

      // Screen shake on big swing quarters
      if (Math.abs(delta) >= 8) {
        triggerShake();
      }
    } else {
      setScoreImpact(null);
    }

    if (data.isComplete) {
      // Check if there's feedback to show before navigating
      if (data.feedback && (data.feedback.narrative || data.feedback.effects?.length)) {
        showPostQuarterFeedback(data.feedback, () => {
          navigateToResults(data);
        });
        return;
      }
      navigateToResults(data);
      return;
    }

    // Show quarter result briefly, then check for feedback / moment
    setPhase("quarter_result");
    setTimeout(() => {
      // Layer 2 & 3: Show outcome feedback if we have it
      if (data.feedback && (data.feedback.effects?.length || data.feedback.callbacks?.length)) {
        showPostQuarterFeedback(data.feedback, () => {
          if (data.moment) {
            setCurrentMoment(data.moment);
            setPhase("moment");
          } else {
            continueGame();
          }
        });
      } else if (data.moment) {
        setCurrentMoment(data.moment);
        setPhase("moment");
      } else {
        continueGame();
      }
    }, 1500);
  };

  const showPostQuarterFeedback = (feedback, onDone) => {
    const effects = feedback.effects || [];
    const callbacks = feedback.callbacks || [];
    const narrative = feedback.narrative || "";

    // Layer 2: Show outcome effects with the real narrative from server
    if (effects.length > 0) {
      setNarrativeText(narrative);
      setOutcomeEffects(effects);
      setPhase("outcome");
      setTimeout(() => {
        // Layer 3: Show callbacks if any
        if (callbacks.length > 0) {
          setCallbackTexts(callbacks);
          setPhase("callback");
          setTimeout(() => {
            setCallbackTexts([]);
            onDone();
          }, 3000);
        } else {
          onDone();
        }
      }, 4000);
    } else if (callbacks.length > 0) {
      setCallbackTexts(callbacks);
      setPhase("callback");
      setTimeout(() => {
        setCallbackTexts([]);
        onDone();
      }, 3000);
    } else {
      onDone();
    }
  };

  const navigateToResults = (data) => {
    const result = data.result;
    const gs = data.gameState || {};

    // On resume, setup is empty — fall back to fields the engine persists
    // inside gameState (see app.py simulate-live/start, which stamps _gameId,
    // homeTeamId, awayTeamId, etc. onto the live_games doc).
    const gameId = setup.gameId || gs._gameId || data.gameId || null;
    const userSide = gs.userSide || "home";
    const homeTeamId = result.homeTeamId || gs.homeTeamId;
    const awayTeamId = result.awayTeamId || gs.awayTeamId;
    const userTeamId =
      setup.userTeamId || (userSide === "home" ? homeTeamId : awayTeamId);

    navigate("/results", {
      state: {
        gameId,
        homeTeamId,
        awayTeamId,
        userTeamId,
        season: result.season || gs.season,
        homeTeam: result.homeTeamName || gs.homeTeamName,
        awayTeam: result.awayTeamName || gs.awayTeamName,
        homeScore: result.homeScore,
        awayScore: result.awayScore,
        homeTeamStats: result.homeTeamStats || [],
        awayTeamStats: result.awayTeamStats || [],
        homeStarterIds: setup.homeStarterIds || [],
        awayStarterIds: setup.awayStarterIds || [],
        tradeProposal: result.tradeProposal,
        quarterScores: result.quarterScores,
        moments: result.moments,
        injuries: result.injuries || [],
      },
    });
  };

  const handleMomentChoice = (option) => {
    const momentId = currentMoment.id;
    const choiceId = option.id;

    // Store decision for after narrative
    pendingDecision.current = { momentId, choiceId };
    setCurrentMoment(null);

    // Layer 1: Show narrative immediately (optimistic — real narrative comes from server)
    setNarrativeText(`You chose: ${option.label}`);
    setPhase("narrative");

    // After showing the choice confirmation, call server
    setTimeout(() => {
      continueGame(momentId, choiceId);
    }, 2000);
  };

  // When server responds after a decision, we get the real narrative
  // This is handled in handleServerResponse via feedback.narrative
  // The narrative phase shows optimistic text; outcome phase shows real effects

  // ── Computed values ────────────────────────────────────────────
  const homeScores = gameState?.quarterScores?.home || [];
  const awayScores = gameState?.quarterScores?.away || [];
  const totalHome = homeScores.reduce((a, b) => a + b, 0);
  const totalAway = awayScores.reduce((a, b) => a + b, 0);
  const currentQuarter = gameState?.currentQuarter || 0;
  const homeTeamName = gameState?.homeTeamName || setup.homeTeamName || "Home";
  const awayTeamName = gameState?.awayTeamName || setup.awayTeamName || "Away";

  // ── Render ─────────────────────────────────────────────────────

  if (error) {
    return (
      <>
        <Navbar />
        <div className="live-game-container">
          <div style={{ textAlign: "center", padding: 40 }}>
            <p style={{ color: "var(--color-text-error)", fontSize: 16 }}>{error}</p>
            <button
              onClick={() => navigate(-1)}
              style={{
                marginTop: 16,
                padding: "10px 24px",
                background: "var(--color-primary)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Go Back
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className={`live-game-container${shaking ? " shake" : ""}`}>
        {/* Scoreboard */}
        <div className="live-scoreboard">
          <div className="scoreboard-teams">
            {/* Away Team */}
            <div className="scoreboard-team">
              <div className="scoreboard-location">AWAY</div>
              <div className="scoreboard-team-name">{awayTeamName}</div>
              <div className="scoreboard-score" key={`away-${totalAway}`}>
                {totalAway}
              </div>
            </div>

            {/* Center */}
            <div className="scoreboard-divider">
              <div className="scoreboard-quarter-label">
                {currentQuarter > 0 ? (
                  phase === "simulating" && currentQuarter < 4
                    ? `Q${currentQuarter + 1}`
                    : `Q${currentQuarter}`
                ) : (
                  "PRE"
                )}
              </div>
              <div className="scoreboard-vs">VS</div>
            </div>

            {/* Home Team */}
            <div className="scoreboard-team">
              <div className="scoreboard-location">HOME</div>
              <div className="scoreboard-team-name">{homeTeamName}</div>
              <div className="scoreboard-score" key={`home-${totalHome}`}>
                {totalHome}
              </div>
            </div>
          </div>

          {/* Quarter Breakdown */}
          <div className="quarter-breakdown">
            {[1, 2, 3, 4].map((q) => {
              const hScore = homeScores[q - 1];
              const aScore = awayScores[q - 1];
              const isCompleted = q <= currentQuarter;
              const isActive =
                phase === "simulating" && q === currentQuarter + 1;

              return (
                <div
                  key={q}
                  className={`quarter-box${isActive ? " active" : ""}${isCompleted ? " completed" : ""}`}
                >
                  <div className="quarter-box-label">Q{q}</div>
                  <div
                    className={`quarter-box-scores${!isCompleted ? " pending" : ""}`}
                  >
                    {isCompleted
                      ? `${aScore} - ${hScore}`
                      : "- - -"}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Phase Content */}
        {phase === "loading" && (
          <div className="simulating-indicator">
            <div className="simulating-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="simulating-text">Setting up...</div>
          </div>
        )}

        {phase === "simulating" && (
          <div className="simulating-indicator">
            <div className="simulating-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div className="simulating-text">
              Simulating Q{Math.min(currentQuarter + 1, 4)}...
            </div>
          </div>
        )}

        {phase === "quarter_result" && (
          <div className="simulating-indicator" style={{ gap: 8 }}>
            <div className="simulating-text" style={{ fontSize: 16, color: "var(--color-text-main)" }}>
              End of Q{currentQuarter}
            </div>
            <div className="simulating-text">
              {awayTeamName} {totalAway} — {homeTeamName} {totalHome}
            </div>
          </div>
        )}

        {/* MOMENT DECISION CARD */}
        {phase === "moment" && currentMoment && (
          <div className="moment-overlay">
            <div className={`moment-card ${currentMoment.category}`}>
              <div className="moment-header">
                <div className={`moment-category ${currentMoment.category}`}>
                  {CATEGORY_ICONS[currentMoment.category] || ""}{" "}
                  {CATEGORY_LABELS[currentMoment.category] || currentMoment.category}
                </div>
                <h2 className="moment-name">{currentMoment.name}</h2>
                <p className="moment-description">{currentMoment.description}</p>
              </div>
              <div className="moment-options">
                {(currentMoment.options || []).map((option) => (
                  <button
                    key={option.id}
                    className="moment-option"
                    data-risk={option.risk || "low"}
                    onClick={() => handleMomentChoice(option)}
                  >
                    <div className="moment-option-label">
                      {option.label}
                      {option.risk && (
                        <span className={`moment-option-risk ${option.risk}`}>
                          {option.risk} risk
                        </span>
                      )}
                      <RiskMeter risk={option.risk || "low"} />
                    </div>
                    <div className="moment-option-desc">{option.description}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* LAYER 1: NARRATIVE CONFIRMATION */}
        {phase === "narrative" && (
          <div className="narrative-overlay">
            <div className="narrative-card">
              <div className="narrative-text">{narrativeText}</div>
              <div className="simulating-dots" style={{ justifyContent: "center", display: "flex", marginTop: 12 }}>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        {/* LAYER 2: OUTCOME REVEAL */}
        {phase === "outcome" && (
          <div className="outcome-overlay">
            <div className={`outcome-card ${
              outcomeEffects.some((e) => e.type === "positive") && !outcomeEffects.some((e) => e.type === "negative")
                ? "positive-overall"
                : outcomeEffects.some((e) => e.type === "negative") && !outcomeEffects.some((e) => e.type === "positive")
                  ? "negative-overall"
                  : ""
            }`}>
              {narrativeText && (
                <div className="outcome-narrative">{narrativeText}</div>
              )}
              <div className="outcome-title">What happened</div>
              <div className="outcome-effects">
                {outcomeEffects.map((effect, i) => (
                  <div key={i} className={`outcome-effect ${effect.type}`}>
                    <span className="outcome-effect-icon">
                      {effect.type === "positive" ? "\u25B2" : effect.type === "negative" ? "\u25BC" : "\u25CF"}
                    </span>
                    <div style={{ flex: 1 }}>
                      <span className="outcome-effect-text">{effect.description}</span>
                      <div className="impact-bar-container">
                        <div
                          className={`impact-bar ${effect.type}`}
                          style={{ width: effect.type === "neutral" ? "30%" : effect.type === "positive" ? "70%" : "60%" }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Score Impact Banner (#4) */}
              {scoreImpact && (
                <div className={`score-impact-banner ${
                  scoreImpact.delta > 0 ? "positive" : scoreImpact.delta < 0 ? "negative" : "neutral"
                }`}>
                  <span className={`score-impact-value ${
                    scoreImpact.delta > 0 ? "positive" : scoreImpact.delta < 0 ? "negative" : "neutral"
                  }`}>
                    {scoreImpact.delta > 0 ? "+" : ""}{scoreImpact.delta}
                  </span>
                  <span className="score-impact-label">
                    point {Math.abs(scoreImpact.delta) === 1 ? "swing" : "swing"} in Q{scoreImpact.quarter}
                    <br />
                    <span style={{ fontSize: 11, opacity: 0.7 }}>
                      You scored {scoreImpact.userScore} — Opponent scored {scoreImpact.oppScore}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* LAYER 3: CALLBACK BANNER */}
        {phase === "callback" && callbackTexts.length > 0 && (
          <div className="callback-overlay">
            <div className="callback-card">
              <div className="callback-label">Delayed Effect</div>
              {callbackTexts.map((text, i) => (
                <div key={i} className="callback-text">{text}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default LiveGame;
